import axios from "axios";
import httpClient from "./httpClient";
import { findActiveDeductions } from "./deduction.service.ts";

const prefix = "/payroll";

/** True when the request was cancelled (AbortController / view change / unmount). */
export function isPayrollFetchAborted(e: unknown): boolean {
  if (axios.isCancel(e)) return true;
  if (!e || typeof e !== "object") return false;
  const err = e as { code?: string; name?: string };
  return (
    err.code === "ERR_CANCELED" ||
    err.name === "CanceledError" ||
    err.name === "AbortError"
  );
}

export async function findPayroll(
  branch: number,
  start: string,
  end: string,
  limit: number = 10,
  offset: number = 0
) {
  const response = await httpClient.get(
    `${prefix}/find-by?branch=${branch}&start=${start}&end=${end}&limit=${limit}&offset=${offset}`
  );

  let newDeductions: Awaited<ReturnType<typeof findActiveDeductions>> = [];
  try {
    newDeductions = await findActiveDeductions({ start, end });
  } catch (error) {
    console.error("Error fetching new deductions:", error);
  }
  return {
    ...response.data,
    newDeductions,
  };
}

export async function findPayrollSummary(
  branch: number,
  start: string,
  end: string,
  limit: number = 10,
  offset: number = 0,
  search: string = '',
  sort: string = 'Chilito',
  order: string = 'asc',
  signal?: AbortSignal,
  partnerIds?: number[]
) {
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
  const sortParam = sort ? `&sort=${encodeURIComponent(sort)}` : '';
  const orderParam = order ? `&order=${encodeURIComponent(order)}` : '';
  const partnerIdsParam =
    partnerIds?.length && partnerIds.length > 0
      ? `&partnerIds=${partnerIds.join(',')}`
      : '';
  const response = await httpClient.get(
    `${prefix}/summary?branch=${branch}&start=${start}&end=${end}&limit=${limit}&offset=${offset}${searchParam}${sortParam}${orderParam}${partnerIdsParam}`,
    { signal }
  );

  return response.data;
}


export async function findChartDaysWorked(
  partner: number,
  branch: number,
  start: string,
  end: string,
  signal?: AbortSignal,
  options?: { refresh?: boolean }
) {
  const refreshParam = options?.refresh ? "&refresh=true" : "";
  const response = await httpClient.get(
    `${prefix}/chart-days-worked?partner=${partner}&branch=${branch}&start=${start}&end=${end}${refreshParam}`,
    { signal }
  );
  return response.data;
}


export async function findTotalIncidents(
  partner: number,
  branch: number,
  start: string,
  end: string,
  signal?: AbortSignal
) {
  const response = await httpClient.get(
    `${prefix}/total-incidents?partner=${partner}&branch=${branch}&start=${start}&end=${end}`,
    { signal }
  );
  return response.data;
}

export type DailyPaymentOverridePayload = {
  partner: number;
  branch: number;
  periodStart: string;
  periodEnd: string;
  workDate: string;
  checkIn: string;
  checkOut: string;
  customConcept?: string;
  paymentAmount: number;
};

export async function upsertDailyPaymentOverride(
  body: DailyPaymentOverridePayload
) {
  const response = await httpClient.post(
    `${prefix}/daily-payment-override`,
    body
  );
  return response.data;
}

export async function deleteDailyPaymentOverride(
  body: Omit<DailyPaymentOverridePayload, "customConcept" | "paymentAmount">
) {
  const response = await httpClient.delete(
    `${prefix}/daily-payment-override`,
    { data: body }
  );
  return response.data;
}

export async function findPaymentDetail(
  partner: number,
  branch: number,
  start: string,
  end: string,
  signal?: AbortSignal,
  options?: { refresh?: boolean }
) {
  const refreshParam = options?.refresh ? "&refresh=true" : "";
  const response = await httpClient.get(
    `${prefix}/payment-detail?partner=${partner}&branch=${branch}&start=${start}&end=${end}${refreshParam}`,
    { signal }
  );
  return response.data;
}

export type PayrollRowDetailsSnapshot = {
  paymentDetail: Record<string, unknown>;
  chartDaysWorked: Record<string, unknown>;
};

const ROW_DETAILS_BATCH_CHUNK_SIZE = 50;

function mergeRowDetailsBatchResponse(
  raw: { byPartnerId?: Record<string, PayrollRowDetailsSnapshot> } | undefined,
  into: Record<number, PayrollRowDetailsSnapshot>
) {
  for (const [key, val] of Object.entries(raw?.byPartnerId || {})) {
    const id = Number(key);
    if (!Number.isFinite(id) || !val) continue;
    into[id] = val;
  }
}

export type PayrollRowDetailsBatchOptions = {
  /** Se invoca tras cada trozo para pintar filas de a poco en la tabla. */
  onChunk?: (partial: Record<number, PayrollRowDetailsSnapshot>) => void;
  /** true = ignorar caché del snapshot y recalcular (tras incidencias / overrides). */
  refresh?: boolean;
};

/** Batch por POST en trozos (cálculo pesado; trozos pequeños evitan timeout). */
export async function findPayrollRowDetailsBatch(
  branch: number,
  start: string,
  end: string,
  partnerIds: number[],
  signal?: AbortSignal,
  options?: PayrollRowDetailsBatchOptions
): Promise<Record<number, PayrollRowDetailsSnapshot>> {
  if (!partnerIds.length) {
    return {};
  }

  const unique = [...new Set(partnerIds.filter((id) => id > 0))];
  const merged: Record<number, PayrollRowDetailsSnapshot> = {};

  for (let i = 0; i < unique.length; i += ROW_DETAILS_BATCH_CHUNK_SIZE) {
    const chunk = unique.slice(i, i + ROW_DETAILS_BATCH_CHUNK_SIZE);
    const raw = (
      await httpClient.post(
        `${prefix}/row-details-batch`,
        {
          branch,
          start,
          end,
          partnerIds: chunk,
          refresh: options?.refresh === true,
        },
        { signal }
      )
    ).data as { byPartnerId?: Record<string, PayrollRowDetailsSnapshot> };
    const partial: Record<number, PayrollRowDetailsSnapshot> = {};
    mergeRowDetailsBatchResponse(raw, partial);
    mergeRowDetailsBatchResponse(raw, merged);
    options?.onChunk?.(partial);
  }

  return merged;
}

export type PayrollDenominationBreakdownRow = {
  partnerId: number;
  branchId: number;
  periodStart: string;
  periodEnd: string;
  counts: Record<string, number>;
  editedDenomination?: string | null;
  editedCount?: number | null;
  updatedAt?: string;
};

export async function findPayrollDenominationBreakdown(
  branch: number,
  start: string,
  end: string
): Promise<{ rows: PayrollDenominationBreakdownRow[] }> {
  const response = await httpClient.get(
    `${prefix}/denomination-breakdown?branch=${branch}&start=${start}&end=${end}`
  );
  return response.data;
}

export type UpsertPayrollDenominationBreakdownPayload = {
  partner: number;
  branch: number;
  periodStart: string;
  periodEnd: string;
  counts?: Record<string, number>;
  editedDenomination: string;
  editedCount: number;
};

export async function upsertPayrollDenominationBreakdown(
  body: UpsertPayrollDenominationBreakdownPayload
) {
  const response = await httpClient.post(`${prefix}/denomination-breakdown`, body);
  return response.data;
}
